import React from 'react'
import {
    useUltraPlayStore,
    useConfigurationStore,
    useBetSliderStore,
    useSpinButtonSectionStore,
    usePlayerInfoStore,
    useBuyBonusStore,
    ULTRA_PLAY_SPIN_OPTIONS,
} from '@stores'
import {
    AutoplayButtonClickedMessage,
    BetSliderSelectedIndexChangedMessage,
    BetOptionItem,
    BuyBonusButtonClickedMessage,
    BuyBonusConfirmButtonClickedMessage,
    BuyBonusOptionButtonClickedMessage,
    EngineBetSliderSetScrollingIndexMessage,
    GameUIMessageScope,
    GameUIMessageType,
    SetSpeedButtonLevelMessage,
    StopAutoplayClickedMessage,
    ViewportType,
} from 'game-ui-types'
import { playUltraPlaySound, ULTRA_PLAY_SOUND } from './ultraPlaySound'

// ── Helpers ───────────────────────────────────────────────────────────────────

const getBetAmount = (betOptions: number[] | BetOptionItem[], index: number): number => {
    if (!betOptions.length) return 0

    const clamped = Math.max(0, Math.min(index, betOptions.length - 1))
    const option = betOptions[clamped]

    return typeof option === 'number' ? option : option.betAmount
}

const findClosestEngineIndex = (engineOptions: number[], targetCount: number): number => {
    if (!engineOptions.length) return 0

    if (targetCount === -1) {
        const endlessIdx = engineOptions.indexOf(-1)

        return endlessIdx !== -1 ? endlessIdx : engineOptions.length - 1
    }

    return engineOptions.reduce((best, count, i) => {
        if (count === -1) return best

        const bestCount = engineOptions[best] === -1 ? Infinity : engineOptions[best]

        return Math.abs(count - targetCount) < Math.abs(bestCount - targetCount) ? i : best
    }, 0)
}

// All Ultra Play controls logic: store wiring, derived view state and the play/pause/bet/spins/buy-
// bonus handlers. Extracted from UltraPlayControls so that component is purely presentational.
export const useUltraPlayControls = () => {
    const status = useUltraPlayStore((s) => s.status)
    const spins = useUltraPlayStore((s) => s.spins)
    const totalBet = useUltraPlayStore((s) => s.totalBet)
    const totalWin = useUltraPlayStore((s) => s.totalWin)
    const remainingSpins = useUltraPlayStore((s) => s.remainingSpins)
    const currentSpinNumber = useUltraPlayStore((s) => s.currentSpinNumber)
    const selectedBetIndex = useUltraPlayStore((s) => s.selectedBetIndex)
    const setSelectedBetIndex = useUltraPlayStore((s) => s.setSelectedBetIndex)
    const selectedSpinsIndex = useUltraPlayStore((s) => s.selectedSpinsIndex)
    const setSelectedSpinsIndex = useUltraPlayStore((s) => s.setSelectedSpinsIndex)
    const stopOnFeature = useUltraPlayStore((s) => s.stopOnFeature)
    const setStopOnFeature = useUltraPlayStore((s) => s.setStopOnFeature)
    const selectedBuyBonusFeatureName = useUltraPlayStore((s) => s.selectedBuyBonusFeatureName)
    const setSelectedBuyBonusFeatureName = useUltraPlayStore((s) => s.setSelectedBuyBonusFeatureName)
    const start = useUltraPlayStore((s) => s.start)
    const continueSession = useUltraPlayStore((s) => s.continueSession)
    const pause = useUltraPlayStore((s) => s.pause)
    const resume = useUltraPlayStore((s) => s.resume)
    const extendRemainingSpins = useUltraPlayStore((s) => s.extendRemainingSpins)

    const betOptions = useConfigurationStore((s) => s.betOptions)
    const setConfiguration = useConfigurationStore((s) => s.setConfiguration)
    const currency = useConfigurationStore((s) => s.currency)
    const hideCents = useConfigurationStore((s) => s.currencyHideCents)
    const hasBuyBonus = useConfigurationStore((s) => s.hasBuyBonus)
    const viewport = useConfigurationStore((s) => s.viewport)
    const paytableData = useConfigurationStore((s) => s.paytableData)
    const gameName = (paytableData.gameName as string) || ''

    const buyBonusFeatures = useBuyBonusStore((s) => s.buyBonusConfig.features)
    const updateCurrentFeatureMultiplier = useBuyBonusStore((s) => s.updateCurrentFeatureMultiplier)
    const setMultiChoiceToggle = useBuyBonusStore((s) => s.setMultiChoiceToggle)
    const updateActiveBetIndex = useBetSliderStore((s) => s.updateActiveIndex)
    const updateScrollToIndex = useBetSliderStore((s) => s.updateScrollToIndex)
    const isSetup = status === 'READY' && spins.length === 0
    const isPlaying = status === 'PLAYING'
    const isPaused = status === 'PAUSED'
    const isActive = isPlaying || isPaused
    const isHistory = status === 'READY' && spins.length > 0
    const isPortrait = viewport === ViewportType.MOBILE_PORTRAIT
    const isLandscape = viewport === ViewportType.MOBILE_LANDSCAPE

    // Portrait only: the gear in the footer reveals the feature settings (Stop on Feature + Buy Bonus)
    // above the play button. Stays open while the footer is shown (playing/paused and the finished
    // history view); only resets when we return to the setup screen.
    const [settingsOpen, setSettingsOpen] = React.useState(false)

    React.useEffect(() => {
        if (isSetup) setSettingsOpen(false)
    }, [isSetup])

    const currentBetAmount = getBetAmount(betOptions, selectedBetIndex)
    const currentSpinsCount = ULTRA_PLAY_SPIN_OPTIONS[selectedSpinsIndex] ?? 20
    const totalSpins = currentSpinNumber + remainingSpins

    const spinsValue = isActive
        ? `${currentSpinNumber}/${currentSpinsCount === -1 ? '∞' : totalSpins}`
        : isHistory
          ? `${currentSpinNumber}/${currentSpinNumber}`
          : String(currentSpinsCount === -1 ? '∞' : currentSpinsCount)

    // Spins step only through the predefined options. While playing you can't lower the total below
    // the spins already played — the floor is the smallest option still above the current spin count.
    const canIncrementSpins = selectedSpinsIndex < ULTRA_PLAY_SPIN_OPTIONS.length - 1
    const canDecrementSpins =
        selectedSpinsIndex > 0 && (!isActive || ULTRA_PLAY_SPIN_OPTIONS[selectedSpinsIndex - 1] > currentSpinNumber)

    // ── Buy Bonus bet options ─────────────────────────────────────────────────
    // Save original betOptions before any buy bonus selection so we can restore on "OFF"

    const originalBetOptionsRef = React.useRef<typeof betOptions | null>(null)

    React.useEffect(() => {
        return () => {
            if (originalBetOptionsRef.current) {
                useConfigurationStore.getState().setConfiguration({ betOptions: originalBetOptionsRef.current })
                useBuyBonusStore.getState().updateCurrentFeatureMultiplier(1)
                useBuyBonusStore.getState().setMultiChoiceToggle(false)
            }
        }
    }, [])

    const handleBuyBonusChange = (featureName: string) => {
        if (featureName === 'OFF') {
            if (originalBetOptionsRef.current) {
                setConfiguration({ betOptions: originalBetOptionsRef.current })
            }

            updateCurrentFeatureMultiplier(1)
            setMultiChoiceToggle(false)
        } else {
            if (originalBetOptionsRef.current === null) {
                originalBetOptionsRef.current = betOptions
            }

            const feature = buyBonusFeatures.find((f) => f.name === featureName)
            const originals = originalBetOptionsRef.current

            if (feature && originals) {
                const multiplied = originals.map((opt) =>
                    typeof opt === 'number'
                        ? opt * feature.multiplier
                        : { ...opt, betAmount: (opt as { betAmount: number }).betAmount * feature.multiplier }
                ) as typeof betOptions

                setConfiguration({ betOptions: multiplied })
                updateCurrentFeatureMultiplier(feature.multiplier)
                setMultiChoiceToggle(true)
            }
        }

        setSelectedBuyBonusFeatureName(featureName)

        // During an active session a buy bonus change pauses the game (like a bet change); the new
        // selection is sent to the engine on resume. Stop on Feature changes never pause.
        if (isActive) {
            pause()
            window.postMessage({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED } as StopAutoplayClickedMessage, '*')
        }
    }

    const sendBuyBonusToEngine = (featureName: string, betIdx: number) => {
        const features = useConfigurationStore.getState().buyBonusConfig.features
        const featureIndex = features.findIndex((f) => f.name === featureName)

        window.postMessage(
            {
                type: GameUIMessageType.BUY_BONUS_BUTTON_CLICKED,
                scope: GameUIMessageScope.BUY_BONUS,
            } as BuyBonusButtonClickedMessage,
            '*'
        )
        window.postMessage(
            {
                type: GameUIMessageType.BUY_BONUS_OPTION_BUTTON_CLICKED,
                featureId: featureName,
            } as BuyBonusOptionButtonClickedMessage,
            '*'
        )
        window.postMessage(
            {
                type: GameUIMessageType.BET_SLIDER_SET_SCROLLING_INDEX,
                currentBetIndex: betIdx,
                shouldPositionList: true,
            } as EngineBetSliderSetScrollingIndexMessage,
            '*'
        )
        updateScrollToIndex(betIdx)
        window.postMessage(
            {
                type: GameUIMessageType.BUY_BONUS_CONFIRM_BUTTON_CLICKED,
                selectedFeatureIndex: featureIndex,
                betIndex: betIdx,
                selectedFeatureName: featureName,
                scope: GameUIMessageScope.BUY_BONUS,
            } as BuyBonusConfirmButtonClickedMessage,
            '*'
        )
    }

    // ── Handlers ─────────────────────────────────────────────────────────────

    const sendBetChange = (newIndex: number) => {
        updateActiveBetIndex(newIndex)
        pause()
        window.postMessage({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED } as StopAutoplayClickedMessage, '*')
        window.postMessage(
            {
                scope: GameUIMessageScope.BET_SLIDER,
                type: GameUIMessageType.BET_SLIDER_SELECTED_INDEX_CHANGED,
                index: newIndex,
                isScrolling: false,
            } as BetSliderSelectedIndexChangedMessage,
            '*'
        )
    }

    const handleBetDecrement = () => {
        if (selectedBetIndex <= 0) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.DECREASE_BET)

        const newIndex = selectedBetIndex - 1

        setSelectedBetIndex(newIndex)

        if (isActive) sendBetChange(newIndex)
    }

    const handleBetIncrement = () => {
        if (selectedBetIndex >= betOptions.length - 1) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.INCREASE_BET)

        const newIndex = selectedBetIndex + 1

        setSelectedBetIndex(newIndex)

        if (isActive) sendBetChange(newIndex)
    }

    const handleSpinsDecrement = () => {
        if (selectedSpinsIndex <= 0) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.NUMBER_SPINS)

        const newIndex = selectedSpinsIndex - 1

        setSelectedSpinsIndex(newIndex)

        if (isActive) {
            const newCount = ULTRA_PLAY_SPIN_OPTIONS[newIndex]

            if (newCount !== -1) {
                const currentNum = useUltraPlayStore.getState().currentSpinNumber
                const currentRemaining = useUltraPlayStore.getState().remainingSpins
                const newRemaining = Math.max(1, newCount - currentNum)

                extendRemainingSpins(newRemaining - currentRemaining)

                if (isPlaying) {
                    useUltraPlayStore.getState().setPendingAutoplayRestart(true)
                    window.postMessage(
                        { type: GameUIMessageType.STOP_AUTOPLAY_CLICKED } as StopAutoplayClickedMessage,
                        '*'
                    )
                }
            }
        }
    }

    const handleSpinsIncrement = () => {
        if (selectedSpinsIndex >= ULTRA_PLAY_SPIN_OPTIONS.length - 1) return

        playUltraPlaySound(ULTRA_PLAY_SOUND.NUMBER_SPINS)

        const newIndex = selectedSpinsIndex + 1

        setSelectedSpinsIndex(newIndex)

        if (isActive) {
            const newCount = ULTRA_PLAY_SPIN_OPTIONS[newIndex]

            if (newCount !== -1) {
                const currentNum = useUltraPlayStore.getState().currentSpinNumber
                const currentRemaining = useUltraPlayStore.getState().remainingSpins
                const newRemaining = Math.max(1, newCount - currentNum)

                extendRemainingSpins(newRemaining - currentRemaining)

                if (isPlaying) {
                    useUltraPlayStore.getState().setPendingAutoplayRestart(true)
                    window.postMessage(
                        { type: GameUIMessageType.STOP_AUTOPLAY_CLICKED } as StopAutoplayClickedMessage,
                        '*'
                    )
                }
            }
        }
    }

    const handlePlay = () => {
        playUltraPlaySound(ULTRA_PLAY_SOUND.PLAY)

        // Not enough funds for the selected bet — show the modal instead of starting a session.
        if (currentBetAmount > 0 && usePlayerInfoStore.getState().balance < currentBetAmount) {
            useUltraPlayStore.getState().setShowInsufficientBalance(true)

            return
        }

        const currentActiveBetIndex = useBetSliderStore.getState().activeIndex

        if (selectedBetIndex !== currentActiveBetIndex) {
            useBetSliderStore.getState().updateActiveIndex(selectedBetIndex)
            window.postMessage(
                {
                    scope: GameUIMessageScope.BET_SLIDER,
                    type: GameUIMessageType.BET_SLIDER_SELECTED_INDEX_CHANGED,
                    index: selectedBetIndex,
                    isScrolling: false,
                } as BetSliderSelectedIndexChangedMessage,
                '*'
            )
        }

        const activeBuyBonus = useUltraPlayStore.getState().selectedBuyBonusFeatureName

        if (activeBuyBonus !== 'OFF') {
            sendBuyBonusToEngine(activeBuyBonus, selectedBetIndex)
        }

        const engineOptions = useConfigurationStore.getState().autoplayOptions
        const engineIndex = findClosestEngineIndex(engineOptions, currentSpinsCount)

        window.postMessage(
            { type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED, payload: engineIndex } as AutoplayButtonClickedMessage,
            '*'
        )

        const sbStore = useSpinButtonSectionStore.getState()

        sbStore.updateAutoplayMode(true)
        sbStore.updateAutoplayCount(currentSpinsCount !== -1 ? currentSpinsCount - 1 : currentSpinsCount)
        sbStore.updateSelectedAutoplayIndex(engineIndex)

        const currentSpeed = useSpinButtonSectionStore.getState().speedButtonLevel

        useUltraPlayStore.getState().setPreviousSpeedLevel(currentSpeed)

        // Jump straight to max speed for Ultra Play (no need to simulate Speed button clicks).
        const maxSpeedLevel = useConfigurationStore.getState().shouldEnableUltraSpeed ? 3 : 2

        window.postMessage(
            { type: GameUIMessageType.SET_SPEED_BUTTON_LEVEL, speedLevel: maxSpeedLevel } as SetSpeedButtonLevelMessage,
            '*'
        )

        // From a finished session (history) → continue: keep the table, totals and spin counter and
        // just add the newly selected spins on top (10/10 + 10 → 11/20; + 50 → 11/60). Fresh otherwise.
        if (isHistory) {
            continueSession(currentSpinsCount)
        } else {
            start(usePlayerInfoStore.getState().balance, currentSpinsCount)
        }
    }

    const handlePlayPause = () => {
        if (isPlaying) {
            pause()
            window.postMessage({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED } as StopAutoplayClickedMessage, '*')
        } else if (isPaused) {
            playUltraPlaySound(ULTRA_PLAY_SOUND.PLAY)
            resume()

            const { remainingSpins: resumeRemaining, selectedBuyBonusFeatureName: resumeBuyBonus } =
                useUltraPlayStore.getState()
            const engineOptions = useConfigurationStore.getState().autoplayOptions
            const resumeEngineIndex = findClosestEngineIndex(engineOptions, resumeRemaining)

            if (resumeBuyBonus !== 'OFF') {
                sendBuyBonusToEngine(resumeBuyBonus, selectedBetIndex)
            }

            window.postMessage(
                {
                    type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED,
                    payload: resumeEngineIndex,
                } as AutoplayButtonClickedMessage,
                '*'
            )

            const sbStore = useSpinButtonSectionStore.getState()

            sbStore.updateAutoplayMode(true)
            sbStore.updateAutoplayCount(resumeRemaining - 1)
            sbStore.updateSelectedAutoplayIndex(resumeEngineIndex)
        } else {
            handlePlay()
        }
    }

    return {
        // view state
        status,
        totalBet,
        totalWin,
        currency,
        hideCents,
        gameName,
        viewport,
        isSetup,
        isPlaying,
        isLandscape,
        isPortrait,
        currentBetAmount,
        spinsValue,
        selectedBetIndex,
        setSelectedBetIndex,
        selectedSpinsIndex,
        setSelectedSpinsIndex,
        betOptions,
        stopOnFeature,
        setStopOnFeature,
        selectedBuyBonusFeatureName,
        buyBonusFeatures,
        hasBuyBonus,
        canDecrementSpins,
        canIncrementSpins,
        settingsOpen,
        setSettingsOpen,
        // handlers
        handleBetDecrement,
        handleBetIncrement,
        handleSpinsDecrement,
        handleSpinsIncrement,
        handleBuyBonusChange,
        handlePlayPause,
    }
}

export type UltraPlayControlsApi = ReturnType<typeof useUltraPlayControls>
